import { Box } from '@chakra-ui/react';
import { PORT_CLASS } from 'constants/bounding-box';
import { RootState, store, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectWidget } from 'modules/Editor/store/infographSelector';
import { WidgetActionState, setHoveredWidgetId } from 'modules/Editor/store/widgetControlSlice';
import {
  selectActiveWidgetIds,
  selectActiveWidgets,
  selectHoveredWidgetId,
  selectWidgetActionState,
} from 'modules/Editor/store/widgetSelector';
import { MouseEvent, memo, useEffect, useMemo, useRef } from 'react';
import { PortTypes } from 'types/flowCore.types';
import { WidgetType } from 'types/widget.types';
import {
  calculateAllParentWidgetOffset,
  getGroupTargetDataFromWidgetId,
  getWidgetPosition,
  getWidgetRotation,
} from 'utils/dom';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { GroupWidgetData } from 'widgets/Widget.types';
import { groupIdCache } from 'widgets/sdk';
import { lineConnectionCache } from 'widgets/sdk/LineConnectionCache';

import { useFlowPort } from '../FlowCore';

const DISABLED_HOVER_BOX_WIDGET_TYPES = [WidgetType.Line];
const PORT_WRAPPER_PADDING = 50;

export const HoverBox = memo(() => {
  const hoveredWidgetId = useAppSelector(selectHoveredWidgetId);
  const widgetActionState = useAppSelector(selectWidgetActionState);

  const showHoverBox = useMemo(() => {
    if (!hoveredWidgetId) return false;

    // For now, only show for flow chart widgets
    const attachedLines = lineConnectionCache.getAttachedLines(hoveredWidgetId);
    if (attachedLines?.length <= 0) return false;

    // Don't hover while drag or resize
    if (widgetActionState !== WidgetActionState.Default) return false;

    // Don't show hover box if there are multiple active widgets
    const activeWidgets = selectActiveWidgets(store.getState());
    if (activeWidgets.length > 1) return false;

    const activeWidgetId = activeWidgets[0]?.groupId || activeWidgets[0]?.responsiveGroupId || activeWidgets[0]?.id;
    if (hoveredWidgetId === activeWidgetId) return false;

    const widgetType = getWidgetTypeFromId(hoveredWidgetId);
    return !DISABLED_HOVER_BOX_WIDGET_TYPES.includes(widgetType);
  }, [hoveredWidgetId, widgetActionState]);

  return (
    <Box data-testid='hover-box-container'>
      <WidgetHoverBox showHoverBox={showHoverBox} />
    </Box>
  );
});

HoverBox.displayName = 'HoverBox';

interface WidgetHoverBoxProps {
  showHoverBox: boolean;
}

const WidgetHoverBox = ({ showHoverBox }: WidgetHoverBoxProps) => {
  const { renderPort, clearPort } = useFlowPort();

  const hoveredWidgetId = useAppSelector(selectHoveredWidgetId);
  const dispatch = useAppDispatch();
  const hoverBoxRef = useRef<HTMLDivElement>(null);
  const portWrapperRef = useRef<HTMLDivElement>(null);

  /**
   * Effect to update position of hover box and port wrapper when hovered widget id changes
   */
  useEffect(() => {
    const storeState = store.getState() as RootState;
    const activeWidgetIds = selectActiveWidgetIds(storeState);
    const isActiveWidgetConnected = lineConnectionCache.isConnected(
      groupIdCache.getParentIdNested(activeWidgetIds[0]) || activeWidgetIds[0] || '',
    );
    if (!hoveredWidgetId || !showHoverBox) {
      // Clear port if no longer hovering and current active widget is not part of flow chart
      if (!isActiveWidgetConnected) {
        clearPort();
      }
      return;
    }

    if (!hoverBoxRef.current) return;

    const groupId = groupIdCache.getParentIdNested(hoveredWidgetId);
    const isGroup = !!groupId && getWidgetTypeFromId(groupId) === WidgetType.Group;

    let groupWidgetData = null;
    let groupTargetData = undefined;
    if (isGroup) {
      groupWidgetData = selectWidget(groupId)(storeState);
      groupTargetData = getGroupTargetDataFromWidgetId(hoveredWidgetId, groupWidgetData as GroupWidgetData);
    }

    const widgetEl = !!groupTargetData ? null : document.getElementById(hoveredWidgetId);
    const widgetPos = widgetEl ? getWidgetPosition(widgetEl) : groupTargetData?.wrapperPos;
    const widgetRotation = widgetEl ? getWidgetRotation(widgetEl) : 0;

    if (!widgetPos) return;
    const parentOffset = widgetEl ? calculateAllParentWidgetOffset(widgetEl) : { leftPx: 0, topPx: 0 };
    widgetPos!.leftPx += parentOffset.leftPx;
    widgetPos!.topPx += parentOffset.topPx;

    // Update position of hover box
    const hoverBoxEl = hoverBoxRef.current;
    hoverBoxEl.style.left = `${widgetPos.leftPx}px`;
    hoverBoxEl.style.top = `${widgetPos.topPx}px`;
    hoverBoxEl.style.width = `${widgetPos.widthPx}px`;
    hoverBoxEl.style.height = `${widgetPos.heightPx}px`;
    hoverBoxEl.style.transform = `rotate(${widgetRotation}deg)`;

    // Update position of port wrapper
    // Same as hoverbox with padding applied to encompass the flow mode ports
    const portWrapperEl = portWrapperRef.current;
    if (!portWrapperEl) return;
    if (!isActiveWidgetConnected && lineConnectionCache.isConnected(hoveredWidgetId)) {
      const padding = PORT_WRAPPER_PADDING;
      portWrapperEl.style.left = `${widgetPos.leftPx - padding}px`;
      portWrapperEl.style.top = `${widgetPos.topPx - padding}px`;
      portWrapperEl.style.width = `${widgetPos.widthPx + padding * 2}px`;
      portWrapperEl.style.height = `${widgetPos.heightPx + padding * 2}px`;
      portWrapperEl.style.transform = `rotate(${widgetRotation}deg)`;

      // Set z-index to 0 to prevent hover box from blocking hover over other widgets
      portWrapperEl.style.zIndex = '0';
      renderPort(widgetEl, PortTypes.Creator, groupTargetData);
    } else {
      portWrapperEl.style.left = `0px`;
      portWrapperEl.style.top = `0px`;
      portWrapperEl.style.width = `0px`;
      portWrapperEl.style.height = `0px`;
    }
  }, [hoveredWidgetId, showHoverBox, renderPort, clearPort]);

  // Reset hovered widget when leaving the widget
  // Note: Currently, will only work for flow chart widgets
  // If we want to add for all widgets, should handle it in the widget base component
  const handleMouseLeave = (e: MouseEvent<HTMLDivElement>) => {
    // Ignore if hovered into port
    const newTarget = e.relatedTarget as HTMLElement;
    if (newTarget && newTarget.classList?.contains(PORT_CLASS)) {
      return;
    }

    // Get the mouse position from the event
    const mouseX = e.clientX;
    const mouseY = e.clientY;

    // Check if the mouse is still within the bounding box of the element
    // i.e. hovered to actual widget
    const boundingBox = (hoverBoxRef.current as HTMLElement)?.getBoundingClientRect();
    const isWithinBoundingBox =
      mouseX >= boundingBox.left &&
      mouseX <= boundingBox.right &&
      mouseY >= boundingBox.top &&
      mouseY <= boundingBox.bottom;
    if (isWithinBoundingBox) {
      return;
    }

    // Clear port of hovered widget unless the current active widget is a flow node, then ports should remain
    const activeWidgetIds = selectActiveWidgetIds(store.getState());
    if (
      !lineConnectionCache.isConnected(groupIdCache.getParentIdNested(activeWidgetIds[0]) || '') ||
      activeWidgetIds[0]
    ) {
      clearPort();
    }
    dispatch(setHoveredWidgetId(null));
  };

  if (!showHoverBox) return null;

  return (
    <>
      {/* div that renders the blue BB around the widget on hover*/}
      <Box
        position='absolute'
        cursor='move'
        ref={hoverBoxRef}
        border='1px solid'
        borderColor='upgrade.500'
        pointerEvents='none'
        zIndex='999'
      />

      {/* div to handle mouse leave events for flow chart nodes */}
      <Box width={0} height={0} ref={portWrapperRef} position='absolute' onMouseLeave={handleMouseLeave} />
    </>
  );
};
